import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/logo";

type AuthorizationDetails = {
  client?: { name?: string; client_id?: string; redirect_uris?: string[] } | null;
  scope?: string | null;
  redirect_url?: string | null;
  redirect_to?: string | null;
};

type OAuthNamespace = {
  getAuthorizationDetails: (id: string) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
  approveAuthorization: (id: string) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
  denyAuthorization: (id: string) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
};

function oauthApi(): OAuthNamespace {
  return (supabase.auth as unknown as { oauth: OAuthNamespace }).oauth;
}

const SCOPE_LABELS: Record<string, string> = {
  openid: "Confirm who you are",
  email: "Share your email address",
  profile: "Share your basic profile",
};

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>) => ({
    authorization_id: typeof search.authorization_id === "string" ? search.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({
        to: "/auth",
        search: { next: location.pathname + location.searchStr },
      });
    }
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get("authorization_id")!;
    const { data, error } = await oauthApi().getAuthorizationDetails(authorizationId);
    if (error) throw new Error(error.message);
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    const { data: sessionData } = await supabase.auth.getSession();
    return { details: data, email: sessionData.session?.user.email ?? null };
  },
  component: Consent,
  errorComponent: ({ error }) => (
    <main className="flex min-h-screen items-center justify-center bg-brand-muted/40 px-4">
      <div className="max-w-md rounded-2xl border border-brand-deep/10 bg-white p-8 text-center shadow-xl">
        <h1 className="text-lg font-semibold text-brand-deep">This authorization request couldn't be loaded</h1>
        <p className="mt-2 text-sm text-brand-deep/60">
          {String((error as Error)?.message ?? error)}
        </p>
        <p className="mt-4 text-sm text-brand-deep/60">
          It may have expired. Start the connection again from the app you were using.
        </p>
      </div>
    </main>
  ),
  head: () => ({
    meta: [
      { title: "Authorize access — MG Content Labs" },
      {
        name: "description",
        content: "Review and approve an application requesting access to your MG Content Labs account.",
      },
      { property: "og:title", content: "Authorize access — MG Content Labs" },
      {
        property: "og:description",
        content: "Approve or deny an app requesting access to your MG Content Labs account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function Consent() {
  const { details, email } = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clientName = details?.client?.name ?? "This application";
  const redirectUri = details?.client?.redirect_uris?.[0] ?? null;
  const scopes = (details?.scope ?? "").split(/\s+/).filter(Boolean);

  async function decide(approve: boolean) {
    setBusy(approve ? "approve" : "deny");
    setError(null);
    const api = oauthApi();
    const { data, error: err } = approve
      ? await api.approveAuthorization(authorization_id)
      : await api.denyAuthorization(authorization_id);
    if (err) {
      setBusy(null);
      setError(err.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(null);
      setError("No redirect was returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-brand-muted/40 px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border border-brand-deep/10 bg-white p-8 shadow-xl">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>

        <h1 className="text-center text-xl font-semibold text-brand-deep">
          Connect {clientName} to MG Content Labs
        </h1>
        <p className="mt-2 text-center text-sm text-brand-deep/60">
          {clientName} will be able to call this app's enabled tools while you are signed in.
        </p>

        <dl className="mt-6 space-y-3 rounded-xl border border-brand-deep/10 bg-brand-muted/30 p-4 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-brand-deep/50">Signed in as</dt>
            <dd className="text-right font-medium text-brand-deep">{email ?? "—"}</dd>
          </div>
          {redirectUri && (
            <div className="flex justify-between gap-4">
              <dt className="text-brand-deep/50">Redirects to</dt>
              <dd className="max-w-[60%] break-all text-right text-brand-deep/80">{redirectUri}</dd>
            </div>
          )}
        </dl>

        {scopes.length > 0 && (
          <ul className="mt-4 space-y-1.5 text-sm text-brand-deep/80">
            {scopes.map((scope) => (
              <li key={scope} className="flex gap-2">
                <span className="text-brand-accent">•</span>
                {SCOPE_LABELS[scope] ?? `Additional permission requested: ${scope}`}
              </li>
            ))}
          </ul>
        )}

        <p className="mt-4 text-xs leading-relaxed text-brand-deep/50">
          This does not bypass this app's permissions or backend policies. {clientName} only sees
          the enquiries and content your own account can access.
        </p>

        {error && (
          <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="mt-6 space-y-2">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => decide(true)}
            className="w-full rounded-full bg-brand-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
          >
            {busy === "approve" ? "Connecting…" : "Approve"}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => decide(false)}
            className="w-full rounded-full border border-brand-deep/15 bg-white px-5 py-2.5 text-sm font-semibold text-brand-deep transition-colors hover:bg-brand-muted/60 disabled:opacity-50"
          >
            {busy === "deny" ? "Cancelling…" : "Cancel connection"}
          </button>
        </div>
      </div>
    </main>
  );
}
